import { Booking } from './../../interfaces/booking';
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { Contact } from '@contacts/interfaces/contact';
import { BookingEvent } from '@bookings/interfaces/booking-event';
import VuexFormProvider from '@shared/components/forms/auto-form/vuex-form-provider/vuex-form-provider';
import StdxForm from '@shared/components/forms/auto-form/stdx-form/stdx-form.vue';
import { Resource } from '@centers/interfaces/resource';
import EventConflicts from '../event-conflicts/event-conflicts.vue';
import { PriceCategory } from '@contacts/interfaces/price-category';
import { Article } from '@accounting/interfaces/article';
import MultiplayerBookingSection from '@bookings/components/booking-multiplayer/multiplayer-booking-section.vue';
import { MultiplayerBooking } from '@bookings/interfaces/multiplayer-booking';
import { Discipline } from '@centers/interfaces/discipline';
import EntitySelect from '@shared/components/forms/inputs/entity-select/entity-select.vue';

@Component({
  name: 'bpc-booking-event-form',
  components: {
    'bpc-stdx-form': StdxForm,
    'bpc-event-conflicts': EventConflicts,
    'bpc-multiplayer-booking-section': MultiplayerBookingSection,
    'bpc-entity-select': EntitySelect,
  },
})
export default class BookingEventForm extends Vue {
  @Prop([String, Number]) readonly entityId: string | number;
  @Prop({ type: String, default: 'default' }) formConfigName: string;
  @Prop({ type: String, default: 'bookingEvent' }) storeName: string;
  @Prop({ type: Number, default: 0 }) readonly deep: number;
  @Prop({ type: String, default: 'api' }) readonly mode: string;
  @Prop({ type: Boolean, default: false }) readonly withDraft: boolean;
  @Prop(Number) readonly bookingId: number;
  @Prop(Number) readonly initialContactId: number;
  @Prop({ type: Boolean, default: false }) readonly disabled: boolean;

  // DATA
  public formModel: BookingEvent = new BookingEvent();
  public checkingPrice: boolean = false;
  public isDirty: boolean = false;
  public initializedFormModel: BookingEvent = new BookingEvent();
  public i: number = 1;
  public conflicts: BookingEvent[] = [];
  public showReplaceConflictsAfterChangeOption: boolean = false;
  public showUpdateOrdersAfterChangeOption: boolean = false;

  get accessControlBadgeConfig() {
    return {
      name: 'access_control_badge_id',
      label: 'bookings.booking_events.access_control_badge.label',
      fieldAttributes: { autocomplete: 'off' },
      wrapperAttributes: { class: { 'inline-field col-md-12': true } },
      selectLabel: 'badge_number',
      selectValue: 'id',
      selectEntity: 'access-control-badge',
      selectMultiple: false,
      formConfigName: 'default-from-entry',
    };
  }

  $refs!: {
    eventForm: InstanceType<typeof StdxForm> & {
      saveEntity(): void;
      formModel: any;
      onDeleteMediaFile(): void;
      searchEntity(): void;
      extraData: string | AnyEntity;
      formProvider: VuexFormProvider;
    };
  };

  // COMPUTED
  public get model(): BookingEvent {
    return this.$store.getters[`${this.storeName}/model`];
  }

  public get status(): string {
    return this.$store.getters[`${this.storeName}/status`];
  }

  public get date(): string {
    return this.$store.getters[`${this.storeName}/date`];
  }

  public get startTime(): string {
    return this.$store.getters[`${this.storeName}/startTime`];
  }

  public get endTime(): string {
    return this.$store.getters[`${this.storeName}/endTime`];
  }

  public get resourceId(): number {
    return this.$store.getters[`${this.storeName}/resourceId`];
  }

  public get contactId(): number {
    return this.$store.getters[`${this.storeName}/contactId`];
  }

  public get contact(): Contact {
    return this.$store.getters[`${this.storeName}/contact`];
  }

  get contactPriceCategory(): PriceCategory {
    return this.$store.getters[`${this.storeName}/contactPriceCategory`];
  }
  get priceCategoryId(): number {
    return this.$store.getters[`${this.storeName}/priceCategoryId`];
  }

  get article(): Article {
    return this.$store.getters[`${this.storeName}/article`];
  }
  get articleId(): number {
    return this.$store.getters[`${this.storeName}/articleId`];
  }

  public get disciplineId(): number {
    return this.$store.getters[`${this.storeName}/disciplineId`];
  }

  public get resource(): Resource {
    return this.$store.getters[`${this.storeName}/resource`];
  }

  public get booking(): Booking {
    return this.$store.getters[`${this.storeName}/booking`];
  }

  public get showConflicts(): boolean {
    return this.$store.getters[`${this.storeName}/showConflicts`];
  }

  public get fieldsRelatedWithConflictStr() {
    return [this.date, this.startTime, this.endTime, this.resourceId].join(',');
  }

  public get newPrice() {
    return this.$store.getters[`${this.storeName}/newPrice`];
  }

  public get originalPrice() {
    return this.$store.getters[`${this.storeName}/originalPrice`];
  }

  public get noRefund() {
    return this.$store.getters[`${this.storeName}/noRefund`];
  }

  public get initializedModel() {
    return this.$store.getters[`${this.storeName}/initialized`];
  }

  public get selectedSuggestedArticles(): Record<string, string[]> {
    return this.$store.getters[`${this.storeName}/selectedSuggestedArticles`];
  }

  public get reductionArticles(): [] {
    return this.$store.getters[`${this.storeName}/reductionArticles`];
  }

  public get ownToRepetitiveBooking(): boolean {
    return this.$store.getters[`${this.storeName}/repetitive`];
  }

  public get isMultiplayer(): boolean {
    return this.$store.getters[`${this.storeName}/isMultiplayer`];
  }

  public get multiplayerBookingData(): MultiplayerBooking {
    return this.$store.getters[`${this.storeName}/multiplayerData`];
  }

  public get discipline(): Discipline {
    return this.$store.getters[`${this.storeName}/discipline`];
  }

  extraData = {
    booking_id: null,
    contact_id: null,
  };

  // LIFE CYCLE
  beforeMount() {
    if (this.bookingId) {
      this.$store.commit(`${this.storeName}/setModelProp`, {
        prop: 'booking_id',
        value: this.bookingId,
      });
      this.extraData.booking_id = this.bookingId;
    }

    if (this.initialContactId) {
      this.extraData.contact_id = this.initialContactId;
    }
  }

  mounted() {
    this.$store.commit(`${this.storeName}/setMode`, this.mode);
    this.$root.$on('updatePriceFromOrders', event => {
      this.calculateTotalPriceFromOrders();
    });
  }

  public async destroyed() {
    await this.$store.dispatch(`${this.storeName}/resetForm`);
    this.$root.$off('updatePriceFromOrders');
  }

  // METHODS
  async onConflictChange() {
    this.$store.commit(`${this.storeName}/setShowReplaceButton`, false);
    this.$store.commit(`${this.storeName}/setShowConflicts`, false);
    return await this.searchAndManageConflicts();
  }

  async searchAndManageConflicts(): Promise<boolean> {
    this.$store.commit(`${this.storeName}/setShowConflicts`, false);

    const conflicts = await this.$store.dispatch(`${this.storeName}/searchConflicts`, {
      date: this.date,
      start_time: this.startTime,
      end_time: this.endTime,
      resource_id: this.resourceId,
      excluded_ids: this.model.id ? [this.model.id] : [],
    });

    this.conflicts = conflicts;

    if (this.conflicts.length > 0 && !this.ownToRepetitiveBooking) {
      // show Conflicts
      if (this.mode === 'storage') {
        this.$store.commit(`${this.storeName}/setModelProp`, {
          prop: 'conflicts',
          value: conflicts,
        });

        this.$store.commit(`${this.storeName}/setModelProp`, {
          prop: 'action',
          value: null,
        });
      }
      this.$store.commit(`${this.storeName}/setShowConflicts`, true);
      this.$store.commit(`${this.storeName}/setShowReplaceButton`, true);
      return false;
    }
    return true;
  }

  async clickOnReplaceButton(): Promise<string> {
    // Send booking with events to be everwriten
    // const override = await this.$store.dispatch('booking/saveAndOverride'); // TODO implement this method in vuex
    if (this.mode === 'storage') {
      this.$store.commit(`${this.storeName}/setModelProp`, {
        prop: 'action',
        value: 'overwrite',
      });
    }
    await this.$refs.eventForm.saveEntity();

    this.$store.commit(`${this.storeName}/setShowReplaceButton`, false);
    this.$store.commit(`${this.storeName}/setShowConflicts`, false);
    return 'replaced';
  }

  async saveEntity() {
    this.$store.commit(`${this.storeName}/setShowConflicts`, false);

    const pass = await this.searchAndManageConflicts();
    if (!pass) {
      return null;
    }
    const res = await this.$refs.eventForm.saveEntity();
    this.$root.$emit('bookingEventSaved', res);
    return 'saved';
  }

  onEventFormSaved(e) {
    this.$emit('entity-saved', e);
  }

  onSavingEntity(e) {
    this.$emit('saving-entity', e);
  }

  customFormatModel(formModel: Contact, saveEntity: Function) {
    saveEntity(formModel);
  }

  private getPriceParamsStringId() {
    return [
      this.date,
      this.startTime,
      this.endTime,
      this.resourceId,
      this.disciplineId,
      this.priceCategoryId,
      this.articleId,
    ].join(',');
  }

  calculatePriceIfNothingChange() {
    if (this.$store.getters[`${this.storeName}/canGetPrice`] && this.isDirty) {
      const oldRelatedPriceParamsStr = this.getPriceParamsStringId();
      setTimeout(() => {
        const newRelatedPriceParamsStr = this.getPriceParamsStringId();
        if (newRelatedPriceParamsStr === oldRelatedPriceParamsStr) {
          this.$store.dispatch(`${this.storeName}/calcPrice`);
        }
      }, 600);
    }
  }

  changePriceCategory() {
    if (this.isDirty) {
      const newPriceCategory = this.contactPriceCategory;

      if (newPriceCategory) {
        this.$store.commit(`${this.storeName}/setModelProp`, {
          prop: 'price_category',
          value: newPriceCategory,
        });

        this.$store.commit(`${this.storeName}/setModelProp`, {
          prop: 'price_category_id',
          value: newPriceCategory.id,
        });
      } else {
        this.$store.commit(`${this.storeName}/setModelProp`, {
          prop: 'price_category',
          value: null,
        });

        this.$store.commit(`${this.storeName}/setModelProp`, {
          prop: 'price_category_id',
          value: null,
        });
      }
    }
  }

  fieldsRelatedWithConflictsChanged() {
    this.$store.commit(`${this.storeName}/setShowReplaceButton`, false);
    this.$store.commit(`${this.storeName}/setShowConflicts`, false);
  }

  setDirty(val = true) {
    this.isDirty = val;
  }

  showSendEmailAfterChangeOption: boolean = false;

  fieldsRelatedWithSendEmailAfterChange() {
    if (this.isDirty && this.entityId) {
      this.showSendEmailAfterChangeOption =
        this.status === 'confirmed' &&
        (this.resourceId !== this.initializedFormModel?.resource_id ||
          this.date !== this.initializedFormModel?.date ||
          (this.startTime &&
            this.startTime.substring(0, 5) !==
              this.initializedFormModel?.start_time.substring(0, 5)) ||
          (this.endTime &&
            this.endTime.substring(0, 5) !== this.initializedFormModel?.end_time.substring(0, 5)));
    }
  }

  public calculateTotalPriceFromOrders() {
    const orders = this.$store.getters[`${this.storeName}/orders`];
    let totalPrice = 0;
    orders
      .filter(order => order.type_value !== 'subvention' && order.status !== 'cancelled')
      .forEach(order => {
        totalPrice += order.total_price;
      });
    this.$store.commit(`${this.storeName}/setPriceFromOrders`, totalPrice);
  }

  checkFieldsRelatedWithUpdateOrdersAfterChange() {
    if (this.isDirty && this.entityId) {
      this.showUpdateOrdersAfterChangeOption =
        this.resourceId !== this.initializedFormModel?.resource_id ||
        this.date !== this.initializedFormModel?.date ||
        this.startTime?.substr(0, 5) !== this.initializedFormModel?.start_time?.substr(0, 5) ||
        this.endTime?.substr(0, 5) !== this.initializedFormModel?.end_time?.substr(0, 5) ||
        this.articleId !== this.initializedFormModel?.article_id ||
        this.priceCategoryId !== this.initializedFormModel?.price_category_id ||
        this.status !== this.initializedFormModel?.status;
    } else {
      this.showUpdateOrdersAfterChangeOption = false;
    }
  }

  checkFieldsRelatedWithReplaceConflictsAfterChange() {
    if (this.isDirty && this.entityId) {
      this.showReplaceConflictsAfterChangeOption =
        (this.resourceId !== this.initializedFormModel?.resource_id ||
          this.date !== this.initializedFormModel?.date ||
          this.startTime?.substr(0, 5) !== this.initializedFormModel?.start_time?.substr(0, 5) ||
          this.endTime?.substr(0, 5) !== this.initializedFormModel?.end_time?.substr(0, 5)) &&
        // If the event has been reduced and contained inside we have to avoid asking
        // First case: start equal and finish before
        !(
          this.startTime?.substr(0, 5) === this.initializedFormModel?.start_time?.substr(0, 5) &&
          this.endTime?.substr(0, 5) < this.initializedFormModel?.end_time?.substr(0, 5)
        ) &&
        // Second case: start later and finish equal
        !(
          this.startTime?.substr(0, 5) > this.initializedFormModel?.start_time?.substr(0, 5) &&
          this.endTime?.substr(0, 5) === this.initializedFormModel?.end_time?.substr(0, 5)
        ) &&
        // Third case: start later and finish before
        !(
          this.startTime?.substr(0, 5) > this.initializedFormModel?.start_time?.substr(0, 5) &&
          this.endTime?.substr(0, 5) < this.initializedFormModel?.end_time?.substr(0, 5)
        );
    } else {
      this.showReplaceConflictsAfterChangeOption = false;
    }
  }

  formatAmount(value: string | number) {
    const emptyIfNull = false;
    const zeroIfNull = true;
    return this.$bpcHelper.formatPrice(value, emptyIfNull, zeroIfNull);
  }

  // WATCHERS
  @Watch('contact', { deep: true })
  onContactChange() {
    this.$store.commit(`${this.storeName}/setModelProp`, {
      prop: 'contact_id',
      value: this.contact?.id || null,
    });
  }
  @Watch('resource', { deep: true })
  onResourceChange() {
    this.$store.commit(`${this.storeName}/setModelProp`, {
      prop: 'resource_id',
      value: this.resource?.id || null,
    });
  }
  @Watch('booking', { deep: true })
  onBookingChange() {
    this.$store.commit(`${this.storeName}/setModelProp`, {
      prop: 'booking_id',
      value: this.booking?.id || this.bookingId ? this.bookingId : null,
    });
  }

  @Watch('date')
  onDateChange() {
    this.fieldsRelatedWithConflictsChanged();
    this.fieldsRelatedWithSendEmailAfterChange();
    this.calculatePriceIfNothingChange();
    this.checkFieldsRelatedWithUpdateOrdersAfterChange();
    this.checkFieldsRelatedWithReplaceConflictsAfterChange();
  }
  @Watch('startTime')
  onStartTimeChange() {
    this.fieldsRelatedWithConflictsChanged();
    this.fieldsRelatedWithSendEmailAfterChange();
    this.calculatePriceIfNothingChange();
    this.checkFieldsRelatedWithUpdateOrdersAfterChange();
    this.checkFieldsRelatedWithReplaceConflictsAfterChange();
  }
  @Watch('endTime')
  onEndTimeChange() {
    this.fieldsRelatedWithConflictsChanged();
    this.fieldsRelatedWithSendEmailAfterChange();
    this.calculatePriceIfNothingChange();
    this.checkFieldsRelatedWithUpdateOrdersAfterChange();
    this.checkFieldsRelatedWithReplaceConflictsAfterChange();
  }
  @Watch('resourceId')
  onResourceIdChange() {
    this.fieldsRelatedWithConflictsChanged();
    this.fieldsRelatedWithSendEmailAfterChange();
    this.calculatePriceIfNothingChange();
    this.checkFieldsRelatedWithUpdateOrdersAfterChange();
    this.checkFieldsRelatedWithReplaceConflictsAfterChange();
  }

  @Watch('contactId')
  onContactIdChange() {
    this.calculatePriceIfNothingChange();
    this.changePriceCategory();
    this.formModel.access_control_badge_id = null;
    this.formModel.accessControlBadge = null;
  }
  @Watch('priceCategoryId')
  onPriceCategoryChange() {
    this.calculatePriceIfNothingChange();
    this.checkFieldsRelatedWithUpdateOrdersAfterChange();
  }
  @Watch('disciplineId')
  onDisciplineIdChange() {
    this.calculatePriceIfNothingChange();
  }
  @Watch('selectedSuggestedArticles', { deep: true })
  onSelectedSuggestedArticlesChange() {
    this.calculatePriceIfNothingChange();
  }
  @Watch('reductionArticles')
  onReductionArticlesChange() {
    this.calculatePriceIfNothingChange();
  }
  @Watch('article')
  onArticleChange() {
    this.$store.commit(`${this.storeName}/setModelProp`, {
      prop: 'article_id',
      value: this.article?.id,
    });
  }
  @Watch('articleId')
  onArticleIdChange() {
    this.calculatePriceIfNothingChange();
    this.checkFieldsRelatedWithUpdateOrdersAfterChange();
  }
  @Watch('status')
  onStatusChange() {
    this.fieldsRelatedWithSendEmailAfterChange();
    this.checkFieldsRelatedWithUpdateOrdersAfterChange();
  }
  @Watch('initializedModel')
  onInitializedModel(value) {
    if (value === true) {
      this.initializedFormModel = JSON.parse(JSON.stringify(this.model));
    }
  }

  @Watch('formModel', { deep: true })
  onFormModelChange() {
    this.$emit('formModelChanged', this.formModel);
  }
}
